import { useState, useEffect, useRef } from "react";
import Navbar from "../../../components/Navbar";
import {
  CloseOutlined,
  ArrowBack,
  PhotoLibraryOutlined
} from "@mui/icons-material";
import {
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  IconButton,
  Chip,
  Modal,
} from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Autoplay,
} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { StarSharp } from "@mui/icons-material";

import { RULES, amenities } from "../constants";
import Collage from "../Collage";
import VILLAS from "../../landing/Villas/constants";
import { Link } from "react-router-dom";
import { useParams, useNavigate } from "react-router-dom";

const Details = ({ json }) => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const [checkIn, setCheckIn] = useState(dayjs());
  const [checkOut, setCheckOut] = useState(dayjs().add(1, "day"));

  const [amenitiesModalOpen, setAmenitiesModalOpen] = useState(false);

  const key = json.name.toLowerCase().replace(/\s/g, "-");
  const path = `/images/photo-tour/${key}`;

  // Get gradient colors based on villa
  const getGradientColors = () => {
    const villaName = json.name.toLowerCase();
    if (villaName.includes("etisha") || villaName.includes("artha")) {
      return "linear-gradient(to bottom, #587F9E, #314a5eff)";
    } else if (villaName.includes("ananda")) {
      return "linear-gradient(to bottom, #878651, #4E697E)";
    } else if (villaName.includes("alaya")) {
      return "linear-gradient(to bottom, #B88362, #4E697E)";
    }
    return "linear-gradient(to bottom, #878651, #4E697E)"; // default to Ananda
  };

  // Get villa display name
  const getVillaDisplayName = () => {
    const villaName = json.name.toLowerCase();
    if (villaName.includes("etisha") || villaName.includes("artha")) {
      return "Etisha";
    } else if (villaName.includes("ananda")) {
      return "Ananda";
    } else if (villaName.includes("alaya")) {
      return "Alaya";
    }
    return "Ananda"; // default
  };

  const prevRef2 = useRef(null);
  const nextRef2 = useRef(null);
  const [isOverlayOpen, setOverlayOpen] = useState(false);

  useEffect(() => {
    if (isOverlayOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOverlayOpen]);

  return (
    <>
      <Box
        sx={{
          background: getGradientColors(),
          py: 4,
        }}
      >
        <Navbar />

        {isOverlayOpen && (
          <Box
            id="overlay"
            sx={{
              position: "fixed",
              width: "100vw",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              height: "100vh",
              background: "#000000",
              zIndex: 9999,
              p: 4,
              overflowY: "auto",
            }}
            onClick={() => setOverlayOpen(false)}
          >
            <Box onClick={(e) => e.stopPropagation()} m={{ xs: 4, md: 8 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={4}
              >
                <Typography color="#fff" variant="h4" mb={2}>
                  Photo Tour
                </Typography>
                <IconButton onClick={() => setOverlayOpen(false)}>
                  <CloseOutlined />
                </IconButton>
              </Stack>
              <Stack spacing={2}>
                <Box pb={8}>
                  {json.overlay.map((collage, idx) => (
                    <Collage key={idx} {...collage} />
                  ))}
                </Box>
              </Stack>
            </Box>
          </Box>
        )}

        <Box width={"90%"} mt={{ xs: 9, md: 12 }} maxWidth={1240} mx="auto">
          <Box m="auto" data-aos="fade-up">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: "black",
                width: 48,
                height: 48,
                transition: "all 0.3s ease",
                border: "1px solid white",
                mb: 8
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              color="rgba(255, 255, 255, 0.8)"
              textAlign="center"
              mt={1}
              sx={{
                fontSize: "0.9rem",
                fontWeight: 600,
                letterSpacing: 2,
                pl: 16,
                pb: 2,
                textTransform: "uppercase",
              }}
            >
              VILLA
            </Typography>
            <Typography
              color="#ffffff"
              textAlign="center"
              mt={1}
              sx={{
                fontFamily: "Amoresa, serif",
                fontSize: { xs: "3rem", md: "4rem", lg: "5rem" },
                fontWeight: 400,
                lineHeight: 1.1,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              {getVillaDisplayName()}
            </Typography>
            <Typography
              color="rgba(255, 255, 255, 0.8)"
              textAlign="center"
              mt={2}
              sx={{
                fontSize: "1.1rem",
                fontWeight: 400,
              }}
            >
              {json.capacity}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              mx="auto"
              width={{ xs: "100%", sm: "fit-content" }}
              pt={4}
            >
              <Button
                variant="contained"
                color="white"
                startIcon={<PhotoLibraryOutlined />}
                onClick={() => setOverlayOpen(true)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 50,
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Photo tour
              </Button>
              {json.airbnbUrl && (
                <Button
                  component="a"
                  href={json.airbnbUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  color="white"
                  startIcon={
                    <img
                      src="/images/about-us/airbnb.webp"
                      alt=""
                      width={20}
                      height={20}
                      style={{ borderRadius: 4 }}
                    />
                  }
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 50,
                    textTransform: "none",
                    transition: "all 0.3s ease",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Book on Airbnb
                </Button>
              )}
            </Stack>
          </Box>

          {/* Image Grid */}
          <Box
            m="auto"
            pt={4}
            data-aos="fade-up"
            data-aos-delay={100}
            display={{ xs: "none", md: "block" }}
          >
            <Box py={{ xs: 0, md: 6 }} m="auto" position="relative">
              <Stack
                direction="row"
                gap={{ xs: 1.5, sm: 3 }}
                alignItems="center"
              >
                <Box flex={2}>
                  <picture>
                    <source srcSet={`${path}/01.webp`} type="image/webp" />
                    <img
                      style={{ borderRadius: 16 }}
                      src={`${path}/01.jpg`}
                      alt="img"
                      width="100%"
                    />
                  </picture>
                </Box>
                <Box flex={1}>
                  <picture>
                    <source srcSet={`${path}/02.webp`} type="image/webp" />
                    <img
                      style={{ borderRadius: 16 }}
                      src={`${path}/02.jpg`}
                      alt="img"
                      width="100%"
                    />
                  </picture>
                </Box>
                <Stack flex={1} gap={{ xs: 1.5, sm: 3 }}>
                  <Box flex={1}>
                    <picture>
                      <source srcSet={`${path}/03.webp`} type="image/webp" />
                      <img
                        style={{ borderRadius: 16 }}
                        src={`${path}/03.jpg`}
                        alt="img"
                        width="100%"
                      />
                    </picture>
                  </Box>
                  <Box flex={1}>
                    <picture>
                      <source srcSet={`${path}/04.webp`} type="image/webp" />
                      <img
                        style={{ borderRadius: 16 }}
                        src={`${path}/04.jpg`}
                        alt="img"
                        width="100%"
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
        <Box
          data-aos="fade-up"
          data-aos-delay={100}
          width="90%"
          m="auto"
          pt={4}
          display={{ xs: "block", md: "none" }}
        >
          <Swiper
            style={{ borderRadius: 24 }}
            modules={[Navigation, Pagination, Scrollbar, A11y, Autoplay]}
            spaceBetween={24}
            slidesPerView={3}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false, // Keeps autoplay even if user interacts
            }}
            breakpoints={{
              1200: { slidesPerView: 3 },
              900: { slidesPerView: 2 },
              0: { slidesPerView: 1 },
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }}
          >
            {json.images.map((src, idx) => (
              <SwiperSlide key={idx}>
                <picture>
                  <source srcSet={`${path}/${src}.webp`} type="image/webp" />
                  <img
                    src={path + "/" + src + ".jpg"}
                    style={{ borderRadius: 24 }}
                    width="100%"
                    alt=""
                  />
                </picture>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>
      <Box maxWidth={1240} m="auto" px={1} mb={6}>
        <Grid container spacing={8}>
          <Grid item xs={12} lg={8}>
            <Box py={6} mt={6}>
              <Typography variant="h3" fontWeight={600} color="text.primary">
                About
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {json.description}
              </Typography>
            </Box>
            <Box py={6}>
              <Typography variant="h3" fontWeight={600} color="text.primary">
                Amenities
              </Typography>
              <Box width="fit-content">
                <Stack
                  direction="row"
                  gap={4}
                  py={6}
                  width="100%"
                  flexWrap="wrap"
                >
                  {amenities.slice(0, 12).map(({ icon, title }, idx) => (
                    <Stack
                      key={idx}
                      borderRadius={3}
                      overflow="hidden"
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ aspectRatio: "1/1" }}
                        width={{ xs: 38, sm: 56 }}
                        border="1px solid #ffffff40"
                        borderRadius={100}
                      >
                        <Box>{icon}</Box>
                      </Stack>
                      <Typography textAlign="center" variant="body1">
                        {title}
                      </Typography>
                    </Stack>
                  ))}

                  {amenities.length > 12 && (
                    <Stack
                      borderRadius={3}
                      overflow="hidden"
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ aspectRatio: "1/1" }}
                        width="56px"
                        border="1px solid #ffffff40"
                        borderRadius={100}
                        bgcolor="#ffffff10"
                      >
                        <Typography variant="body1" color="accent.main">
                          +{amenities.length - 12}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                {amenities.length > 12 && (
                  <Button
                    variant="outlined"
                    color="white"
                    sx={{ px: 6, borderColor: "#fff" }}
                    onClick={() => setAmenitiesModalOpen(true)}
                  >
                    Show more
                  </Button>
                )}
              </Box>
            </Box>
            <Box py={6}>
              <Typography variant="h3" fontWeight={600} color="text.primary">
                Reviews
              </Typography>
              <Box mt={4}>
                <Swiper
                  modules={[Navigation, Pagination, Scrollbar, A11y, Autoplay]}
                  spaceBetween={24}
                  slidesPerView={1}
                  autoplay
                  navigation={{
                    prevEl: prevRef2.current,
                    nextEl: nextRef2.current,
                  }}
                  onInit={(swiper) => {
                    swiper.params.navigation.prevEl = prevRef2.current;
                    swiper.params.navigation.nextEl = nextRef2.current;
                    swiper.navigation.init();
                    swiper.navigation.update();
                  }}
                >
                  {json.testimonials.map((elem, idx) => (
                    <SwiperSlide key={idx}>
                      <Stack
                        bgcolor="#000"
                        sx={{ border: "1px solid #ffffff40" }}
                        p={5}
                        borderRadius={8}
                        spacing={2}
                        flex={1}
                        height="100%"
                      >
                        <Typography>{elem.body}</Typography>
                        <Stack direction="row" alignItems="center" gap={2}>
                          <picture>
                            <source
                              srcSet={`${elem.avatar}.webp`}
                              type="image/webp"
                            />
                            <img
                              src={`${elem.avatar}.avif`}
                              height={64}
                              width={64}
                              style={{ borderRadius: 100 }}
                              alt=""
                            />
                          </picture>
                          <Stack>
                            <Typography>{elem.name}</Typography>
                            <Stack direction="row" spacing={0}>
                              {[1, 2, 3, 4, 5].map((elem, idx) => (
                                <StarSharp color="accent" fontSize="12" />
                              ))}
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            </Box>
            <Box py={6}>
              <Typography variant="h3" fontWeight={600} color="text.primary">
                Location
              </Typography>
              <Box
                mt={4}
                overflow="hidden"
                borderRadius={4}
                height="fit-content"
              >
                <iframe
                  title="map"
                  src={json.location}
                  width="100%"
                  height="600"
                  allowFullScreen=""
                  loading="lazy"
                  style={{ border: "none" }}
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </Box>
            </Box>
            <Box py={6} pb={{ xs: 0, sm: 6 }}>
              <Typography variant="h3" fontWeight={600} color="text.primary">
                House rules
              </Typography>
              <Stack mt={2} gap={2}>
                {RULES.map(({ rule }, idx) => (
                  <Stack direction="row" key={idx} gap={1}>
                    <Typography variant="body1" color="text.secondary">
                      {idx + 1 + ". " + rule}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Box
              borderRadius={6}
              mt={{ xs: 0, sm: 8 }}
              mb={{ xs: 6, sm: 8 }}
              bgcolor="#ffffff20"
              position="sticky"
              top="14.5%"
              p={4}
              sx={{
                boxShadow: "rgba(0, 0, 0, 0.12) 0px 6px 16px",
              }}
            >
              <form name={key} method="POST" data-netlify="true">
                <input type="hidden" name="form-name" value={key} />
                <Stack spacing={2} borderRadius={2} width="100%">
                  <Typography
                    variant="h5"
                    fontWeight={400}
                    color="text.primary"
                  >
                    Check availability
                  </Typography>
                  <TextField
                    sx={{ width: "100%" }}
                    label="Full Name"
                    name="full-name"
                    variant="outlined"
                    required
                  />
                  <TextField
                    label="Phone"
                    name="phone"
                    type="text"
                    variant="outlined"
                    required
                  />
                  <TextField
                    label="Guests"
                    placeholder="No. of guests"
                    name="guest-count"
                    type="number"
                    variant="outlined"
                    required
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DatePicker", "DatePicker"]}>
                      <Stack direction="row" spacing={2}>
                        <DatePicker
                          label="Check in"
                          value={checkIn}
                          onChange={(newValue) => setCheckIn(newValue)}
                          format="DD/MM/YYYY"
                          slotProps={{
                            textField: {
                              name: "check-in",
                              required: true,
                            },
                          }}
                        />
                        <DatePicker
                          label="Check out"
                          value={checkOut}
                          onChange={(newValue) => setCheckOut(newValue)}
                          format="DD/MM/YYYY"
                          slotProps={{
                            textField: {
                              name: "check-out",
                              required: true,
                            },
                          }}
                        />
                      </Stack>
                    </DemoContainer>
                  </LocalizationProvider>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    color="white"
                  >
                    Send enquiry
                  </Button>
                </Stack>
              </form>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        width={{ xs: "90%", lg: "100%" }}
        maxWidth={1240}
        m="auto"
        mb={{ xs: 12, sm: 16 }}
      >
        <Typography
          typography={{ xs: "h3", sm: "h2" }}
          style={{ fontWeight: 600 }}
          color="text.primary"
        >
          Explore our other villas
        </Typography>
        <Grid
          container
          mt={{ xs: 0, sm: 4 }}
          spacing={4}
          columns={{ xs: 4, md: 12 }}
        >
          {VILLAS.map((elem, idx) => (
            <Grid item xs={4}>
              <Link
                to={elem.comingSoon ? "/" : elem.link}
                style={{ textDecoration: "none" }}
              >
                <Box
                  data-aos="fade-up"
                  data-aos-delay={idx * 100}
                  sx={{
                    overflow: "hidden",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  {elem.comingSoon && (
                    <Chip
                      label="Coming soon"
                      color="white"
                      sx={{ position: "absolute", right: 12, top: 12 }}
                    />
                  )}
                  {slug === elem.key && (
                    <Chip
                      label="Currently viewing"
                      color="white"
                      sx={{ position: "absolute", right: 12, top: 12 }}
                    />
                  )}
                  <picture>
                    <source srcSet={`${elem.img}.webp`} type="image/webp" />
                    <img
                      src={`${elem.img}.jpg`}
                      width="100%"
                      style={{ borderRadius: 24 }}
                      alt=""
                    />
                  </picture>
                  <Stack mt={2}>
                    <Typography variant="h5">{elem.title}</Typography>
                    <Typography variant="body1" color="text.secondary">
                      {elem.description}
                    </Typography>
                  </Stack>
                </Box>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Amenities Modal */}
      <Modal
        open={amenitiesModalOpen}
        onClose={() => setAmenitiesModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background: "rgba(255, 255, 255, 0.2)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: { xs: "90%", sm: "80%", md: "70%" },
            maxWidth: "400px",
            maxHeight: "60vh",
            bgcolor: "#000000",
            borderRadius: 4,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          {/* Close Button */}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p={4}
          >
            {/* Modal Content */}
            <Typography variant="h4" fontWeight={600}>
              All Amenities
            </Typography>

            <IconButton
              onClick={() => setAmenitiesModalOpen(false)}
              sx={{
                mb: 1,
                bgcolor: "rgba(255,255,255, 0.2)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255, 0.4)",
                },
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Stack>

          <Box overflow="auto" maxHeight="50vh" pl={4}>
            <Grid container spacing={3}>
              {amenities.map(({ icon, title }, idx) => (
                <Grid item xs={12} key={idx}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    borderRadius={2}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{ aspectRatio: "1/1" }}
                      width={48}
                      border="1px solid #ffffff20"
                      borderRadius={100}
                      bgcolor="white"
                    >
                      <Box>{icon}</Box>
                    </Stack>
                    <Typography variant="body1" color="text.primary">
                      {title}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default Details;
